/* eslint-disable @scandipwa/scandipwa-guidelines/file-structure */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/scandipwa
 * @link https://github.com/scandipwa/scandipwa
 */

import PropTypes from 'prop-types';
import { cloneElement } from 'react';

import { Addresstype } from 'SourceType/Account.type';

import {
    updateBillingSameAsShipping,
    updateClientType,
    updateCustomAddressExpanded
} from '../store/CheckoutFields/CheckoutFields.action';

const propTypes = (member) => ({
    ...member,
    shippingAddress: Addresstype,
    billingAddress: Addresstype,
    billingAddressId: PropTypes.number.isRequired,
    billingSameAsShipping: PropTypes.bool.isRequired,
    clientType: PropTypes.string.isRequired,
    isCustomAddressExpanded: PropTypes.bool.isRequired
});

const defaultProps = (member) => ({
    ...member,
    shippingAddress: null,
    billingAddress: null
});

const passAdressesProps = (args, callback, instance) => {
    const {
        billingAddress,
        clientType,
        shippingAddress,
        billingSameAsShipping,
        updateCustomAddressExpanded,
        isCustomAddressExpanded
    } = instance.props;

    return {
        ...callback(...args),
        billingAddress,
        shippingAddress,
        billingSameAsShipping,
        clientType,
        updateCustomAddressExpanded,
        isCustomAddressExpanded
    };
};

const passAdressesPropsToForm = (args, callback, instance) => {
    const { shippingAddress, billingAddress } = instance.props;
    const prevRender = callback(...args);

    return cloneElement(
        prevRender,
        {
            ...prevRender.props,
            address: shippingAddress || {},
            billingAddress: billingAddress || {},
            totalsRegionId: shippingAddress?.region_id
        }
    );
};

const handleShowSecondAddressForm = (args, callback, instance) => {
    const { updateBillingSameAsShipping } = instance.props;
    const { showSecondAddressForm } = instance.state;

    updateBillingSameAsShipping(showSecondAddressForm);

    callback(...args);
};

const getDefaultAddress = (args, callback) => {
    const { isBilling, billingAddressId } = args?.[0];

    if (!isBilling && billingAddressId) {
        return billingAddressId;
    }

    return callback(...args);
};

const setStateFromProps = (args, _callback) => {
    const { billingSameAsShipping, clientType } = args[0];

    return {
        showSecondAddressForm: !billingSameAsShipping,
        clientType
    };
};

const setStateFromPropsComponent = (args, callback) => {
    if (args[0]?.isCustomAddressExpanded === true) {
        const { onAddressSelect } = args[0];
        onAddressSelect({});

        return {
            ...callback(...args),
            isCustomAddressExpanded: true
        };
    }

    return {
        ...callback(...args)
    };
};

const mapStateToProps = (args, callback, _instance) => {
    const [state] = args;

    return {
        ...callback(...args),
        billingAddress: state.CheckoutFieldsReducer.billingAddress,
        billingAddressId: state.CheckoutFieldsReducer.billingAddressId,
        billingAddressFields: state.CheckoutFieldsReducer.billingAddressFields,
        shippingAddressFields: state.CheckoutFieldsReducer.shippingAddressFields,
        billingSameAsShipping: state.CheckoutFieldsReducer.billingSameAsShipping,
        isCustomAddressExpanded: state.CheckoutFieldsReducer.isCustomAddressExpanded,
        clientType: state.CheckoutFieldsReducer.clientType
    };
};

const mapDispatchToProps = (args, callback, _instance) => {
    const [dispatch] = args;

    return {
        ...callback(...args),
        updateBillingSameAsShipping: (value) => dispatch(updateBillingSameAsShipping(value)),
        updateClientType: (value) => dispatch(updateClientType(value)),
        updateCustomAddressExpanded: (value) => dispatch(updateCustomAddressExpanded(value))
    };
};

const handleChangeClientType = (args, callback, instance) => {
    const { updateClientType } = instance.props;

    updateClientType(args[0]?.target?.value);

    callback(...args);
};

const expandCustomAddress = (args, callback, instance) => {
    const { updateCustomAddressExpanded } = instance.props;

    updateCustomAddressExpanded(true);

    callback(...args);
};

const onAddressSelect = (args, callback, instance) => {
    const { updateCustomAddressExpanded } = instance.props;

    if (Object?.keys(args[0])?.length > 0) {
        updateCustomAddressExpanded(false);
    }

    callback(...args);
};

export default {
    'Component/CheckoutAddressBook/Container': {
        'static-member': {
            propTypes,
            defaultProps,
            getDerivedStateFromProps: setStateFromProps,
            _getDefaultAddressId: getDefaultAddress
        },
        'member-function': {
            containerProps: passAdressesProps,
            handleShowSecondAddressForm,
            handleChangeClientType,
            onAddressSelect
        }
    },
    'Component/CheckoutAddressBook/Container/mapStateToProps': {
        function: mapStateToProps
    },
    'Component/CheckoutAddressBook/Container/mapDispatchToProps': {
        function: mapDispatchToProps
    },
    'Component/CheckoutAddressBook/Component': {
        'static-member': {
            propTypes,
            getDerivedStateFromProps: setStateFromPropsComponent
        },
        'member-function': {
            renderCustomAddress: passAdressesPropsToForm,
            expandCustomAddress
        }
    }
};
